import { useState, useEffect, useCallback, useRef } from "react";

import { TOUR_STEPS, TOUR_STEP_COUNT } from "@optidev/shared-types";

import { analytics } from "@lib/analytics";
import { TOUR_EVENTS } from "@/lib/analytics/onboarding-events";
import { tourApi } from "@/lib/api/tour";
import { resolveTourAnchor, tourStepIdAt } from "@/stores/tour-store";

import { useAuthToken } from "./useAuthToken";

/**
 * Escape hatch for the E2E suite. The tour renders a fixed-position card over the
 * editor, so leaving it running would intercept clicks and flake tests the same
 * way the old onboarding checklist did. Set from
 * `e2e/lib/auth/inject-firebase-auth.ts` at document-start of every navigation.
 *
 * Not a user-facing setting: real suppression is the server's `showTour`.
 */
const E2E_SUPPRESS_KEY = "optidev:tour:suppressed";

/**
 * How long to wait for a step's anchor to mount before giving up on it.
 *
 * A missing anchor means one of two very different things, and they are only
 * distinguishable by waiting. On a cold open the preview wrapper mounts a moment
 * after the editor shell, so treating absence as "skip" immediately made the
 * preview step disappear. On mobile the preview genuinely is not mounted (it sits
 * behind a tab) and never will be, so waiting forever would stall the tour.
 */
const ANCHOR_WAIT_MS = 6000;
const ANCHOR_POLL_MS = 150;

function isSuppressedForTesting(): boolean {
  try {
    return localStorage.getItem(E2E_SUPPRESS_KEY) === "true";
  } catch {
    return false;
  }
}

export interface UseEditorTourReturn {
  /** True when a step should be on screen. */
  isActive: boolean;
  /** Zero based index of the visible step. */
  step: number;
  stepCount: number;
  /** False while waiting for the current step's anchor to mount. */
  isAnchorReady: boolean;
  next: () => void;
  back: () => void;
  skip: () => void;
}

/**
 * Drives the three step editor tour.
 *
 * Runs once per user, resuming at whatever step was last persisted so a reload
 * mid-tour does not restart or lose it. Every write is fire and forget; the UI
 * moves immediately and a failed persist costs at most a replayed step.
 */
export function useEditorTour(): UseEditorTourReturn {
  const { token, loading: tokenLoading } = useAuthToken();
  const [isActive, setIsActive] = useState(false);
  const [step, setStep] = useState(0);
  const [isAnchorReady, setIsAnchorReady] = useState(false);
  // Bumped by the poll below purely to re-run the anchor check.
  const [pollTick, setPollTick] = useState(0);
  const fetched = useRef(false);
  const shownSteps = useRef<Set<number>>(new Set());
  const waitStartedAt = useRef<number | null>(null);

  // Ask the server once per mount whether this user is owed the tour.
  useEffect(() => {
    if (tokenLoading || !token || fetched.current) return;
    if (isSuppressedForTesting()) return;
    fetched.current = true;

    tourApi
      .get(token)
      .then((res) => {
        if (!res.showTour) return;
        setStep(res.step);
        setIsActive(true);
      })
      .catch((err) => {
        // A tour is not worth surfacing an error for. Staying silent means the
        // user simply does not see it this session.
        console.error("Failed to load editor tour state:", err);
      });
  }, [token, tokenLoading]);

  const persist = useCallback(
    (dto: Parameters<typeof tourApi.update>[1]) => {
      if (!token) return;
      tourApi.update(token, dto).catch(() => {
        // Fire and forget by design, see the hook comment.
      });
    },
    [token],
  );

  const finish = useCallback(
    (reason: "completed" | "skipped", lastStep: number) => {
      setIsActive(false);
      setIsAnchorReady(false);
      persist(reason === "skipped" ? { skipped: true } : { completed: true });
      analytics.track(
        reason === "skipped" ? TOUR_EVENTS.SKIPPED : TOUR_EVENTS.COMPLETED,
        { step: TOUR_STEPS[lastStep], step_index: lastStep },
      );
    },
    [persist],
  );

  // Resolve the current step's anchor, waiting for a late mount and moving on if
  // it never arrives. Also fires the one-per-step shown event.
  useEffect(() => {
    if (!isActive) return;

    const anchor = resolveTourAnchor(tourStepIdAt(step));

    if (anchor) {
      waitStartedAt.current = null;
      setIsAnchorReady(true);

      if (!shownSteps.current.has(step)) {
        shownSteps.current.add(step);
        analytics.track(TOUR_EVENTS.STEP_SHOWN, {
          step: TOUR_STEPS[step],
          step_index: step,
        });
      }
      return;
    }

    setIsAnchorReady(false);

    if (waitStartedAt.current === null) {
      waitStartedAt.current = Date.now();
    }

    if (Date.now() - waitStartedAt.current >= ANCHOR_WAIT_MS) {
      waitStartedAt.current = null;
      if (step + 1 >= TOUR_STEP_COUNT) {
        finish("completed", step);
      } else {
        setStep(step + 1);
        persist({ step: step + 1 });
      }
      return;
    }

    const timer = setTimeout(() => setPollTick((v) => v + 1), ANCHOR_POLL_MS);
    return () => clearTimeout(timer);
  }, [isActive, step, pollTick, finish, persist]);

  const next = useCallback(() => {
    analytics.track(TOUR_EVENTS.STEP_ADVANCED, {
      step: TOUR_STEPS[step],
      step_index: step,
    });

    if (step + 1 >= TOUR_STEP_COUNT) {
      finish("completed", step);
      return;
    }

    waitStartedAt.current = null;
    setStep(step + 1);
    persist({ step: step + 1 });
  }, [step, finish, persist]);

  const back = useCallback(() => {
    if (step === 0) return;
    waitStartedAt.current = null;
    setStep(step - 1);
    persist({ step: step - 1 });
  }, [step, persist]);

  const skip = useCallback(() => {
    finish("skipped", step);
  }, [finish, step]);

  return {
    isActive,
    step,
    stepCount: TOUR_STEP_COUNT,
    isAnchorReady,
    next,
    back,
    skip,
  };
}
